import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import "./PostAppointmentFeedback.css";
import { useFormTracking } from "../hooks/useFormTracking";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5050";

const PostAppointmentFeedback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appointmentData, setAppointmentData] = useState(null);
  const [actions, setActions] = useState({});

  // Form state
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedbackCategories, setFeedbackCategories] = useState([]);
  const [feedbackComment, setFeedbackComment] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [favorite, setFavorite] = useState(false);
  const [selectedTipPercentage, setSelectedTipPercentage] = useState(null);
  const [customTipAmount, setCustomTipAmount] = useState("");
  const [makeDefaultTip, setMakeDefaultTip] = useState(false);

  // Submission states
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Form tracking
  const {
    handleFieldFocus,
    handleFieldBlur,
    handleFieldChange,
    handleSubmit: trackFormSubmit,
    handleError: trackFormError,
  } = useFormTracking(
    "post_appointment_feedback",
    "post-appointment-feedback-form",
  );

  const feedbackOptions = [
    { id: "professional", label: "Above and beyond" },
    { id: "clean_room", label: "Clean room" },
    { id: "good_technique", label: "Good technique" },
    { id: "friendly", label: "Friendly therapist" },
  ];

  const tipPercentages = [
    { value: 15, label: "15%" },
    { value: 20, label: "20%" },
    { value: 25, label: "25%" },
  ];

  // Validate token and fetch appointment data
  useEffect(() => {
    if (!token) {
      setError("Invalid link. Please check your SMS for the correct link.");
      setLoading(false);
      return;
    }

    const validateToken = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/post-appointment/validate/${token}`,
        );

        if (response.data.success) {
          setAppointmentData(response.data.data);
          setActions(response.data.data.actions);
          setFavorite(response.data.data.actions.favoritedTherapist);
          setRating(response.data.data.actions.ratingGiven || 0);
        } else {
          setError("Invalid or expired link");
        }
      } catch (err) {
        console.error("Error validating token:", err);
        setError(
          err.response?.data?.message ||
            "This link has expired or is invalid. Please contact support.",
        );
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  // Calculate tip amount based on appointment cost (you may need to adjust this)
  const calculateTipAmount = (percentage) => {
    // Assuming average massage cost is $80 - adjust based on actual appointment data
    const baseAmount = appointmentData?.booking?.amount || 80;
    return (baseAmount * percentage) / 100;
  };

  const handleCategoryToggle = (categoryId) => {
    setFeedbackCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((c) => c !== categoryId)
        : [...prev, categoryId],
    );
  };

  const handleTipSelect = (percentage) => {
    setSelectedTipPercentage(percentage);
    setCustomTipAmount("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // 1. Submit feedback and rating
      if (rating > 0 && !actions.feedbackGiven) {
        await axios.post(`${API_URL}/api/post-appointment/feedback`, {
          token,
          rating,
          feedbackCategories,
          feedbackComment,
          anonymous,
        });
        // Track successful form submit
        trackFormSubmit(true);
      } else {
        trackFormSubmit(false);
        trackFormError("rating", "validation_error", "Rating is required");
      }

      // 2. Update favorite status
      if (favorite !== actions.favoritedTherapist) {
        await axios.post(`${API_URL}/api/post-appointment/favorite`, {
          token,
          favorite,
        });
      }

      // 3. Process tip if selected
      if ((selectedTipPercentage || customTipAmount) && !actions.tipGiven) {
        const tipAmount = customTipAmount
          ? parseFloat(customTipAmount)
          : calculateTipAmount(selectedTipPercentage);

        try {
          await axios.post(`${API_URL}/api/post-appointment/tip`, {
            token,
            amount: tipAmount,
            percentage: selectedTipPercentage,
          });
        } catch (tipError) {
          console.error("Error processing tip:", tipError);
          // Show user-friendly message for payment method errors
          if (
            tipError.response?.status === 400 &&
            tipError.response?.data?.message?.includes("payment method")
          ) {
            alert(
              "Unable to process tip: No saved payment method found. Please save a payment method in your account to leave tips.",
            );
          } else {
            alert(
              `Unable to process tip: ${
                tipError.response?.data?.message ||
                "Payment failed. Please try again later."
              }`,
            );
          }
          // Continue with rest of submission even if tip fails
        }
      }

      setSubmitted(true);

      // Redirect to success or dashboard after 2 seconds
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    } catch (err) {
      console.error("Error submitting feedback:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Error submitting feedback. Please try again.";
      setError(errorMessage);
      // Track form error
      trackFormSubmit(false);
      trackFormError("form", "submit_error", errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="post-appointment-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="post-appointment-container">
        <div className="error-container">
          <h2>Oops!</h2>
          <p>{error}</p>
          <button onClick={() => navigate("/")} className="btn-primary">
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="post-appointment-container">
        <div className="success-container">
          <div className="success-icon">✓</div>
          <h2>Thank you for your feedback!</h2>
          <p>Your feedback helps us improve our services.</p>
          <p className="redirect-text">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  const { therapist, booking } = appointmentData || {};

  return (
    <div className="post-appointment-container">
      <div className="post-appointment-card">
        {/* Header */}
        <button className="close-btn" onClick={() => navigate("/dashboard")}>
          ✕
        </button>

        <div className="header-section">
          <h1 className="appointment-title">
            Your appointment with {therapist?.fullName}
          </h1>
          <p className="appointment-subtitle">
            {booking?.serviceName} •{" "}
            {new Date(booking?.appointmentDate).toLocaleDateString()}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Rating Section */}
          <div className="section">
            <h2 className="section-title">How was your session?</h2>
            <div className="rating-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={`star ${
                    star <= (hoverRating || rating) ? "star-filled" : ""
                  }`}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(star)}
                  disabled={actions.feedbackGiven}>
                  ★
                </button>
              ))}
            </div>
          </div>

          {/* Feedback Categories */}
          <div className="section">
            <h3 className="subsection-title">What went well?</h3>
            <div className="feedback-chips">
              {feedbackOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`chip ${
                    feedbackCategories.includes(option.id)
                      ? "chip-selected"
                      : ""
                  }`}
                  onClick={() => handleCategoryToggle(option.id)}
                  disabled={actions.feedbackGiven}>
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Optional Comment */}
          <div className="section">
            <button
              type="button"
              className="anonymous-feedback-btn"
              onClick={() => setAnonymous(!anonymous)}
              disabled={actions.feedbackGiven}>
              <span className="checkbox-icon">{anonymous ? "☑" : "☐"}</span>
              Leave anonymous feedback
            </button>

            <textarea
              className="feedback-textarea"
              placeholder="Additional comments (optional)"
              value={feedbackComment}
              onChange={(e) => setFeedbackComment(e.target.value)}
              maxLength={2000}
              rows={4}
              disabled={actions.feedbackGiven}
            />
          </div>

          {/* Tipping Section */}
          <div className="section tip-section">
            <div className="therapist-info">
              {therapist?.photo && (
                <img
                  src={therapist.photo}
                  alt={therapist.fullName}
                  className="therapist-photo"
                />
              )}
              <div>
                <h3 className="tip-title">
                  Your tip for {therapist?.fullName}
                </h3>
                <p className="tip-subtitle">
                  100% of tips go to therapists
                  {!appointmentData?.booking?.amount &&
                    " • Requires saved payment method"}
                </p>
              </div>
            </div>

            <div className="tip-options">
              {tipPercentages.map((tip) => (
                <button
                  key={tip.value}
                  type="button"
                  className={`tip-btn ${
                    selectedTipPercentage === tip.value
                      ? "tip-btn-selected"
                      : ""
                  }`}
                  onClick={() => handleTipSelect(tip.value)}
                  disabled={actions.tipGiven}>
                  <span className="tip-percentage">{tip.label}</span>
                  <span className="tip-amount">
                    ${calculateTipAmount(tip.value).toFixed(2)}
                  </span>
                </button>
              ))}
            </div>

            {/* Custom tip amount */}
            <div className="custom-tip">
              <label htmlFor="customTip" className="custom-tip-label">
                Or enter custom amount:
              </label>
              <input
                id="customTip"
                type="number"
                className="custom-tip-input"
                placeholder="0.00"
                value={customTipAmount}
                onChange={(e) => {
                  setCustomTipAmount(e.target.value);
                  setSelectedTipPercentage(null);
                }}
                min="1"
                step="0.01"
                disabled={actions.tipGiven}
              />
            </div>

            {actions.tipGiven && (
              <p className="tip-given-notice">
                ✓ You've already left a tip of ${actions.tipAmount?.toFixed(2)}
              </p>
            )}
          </div>

          {/* Favorite Therapist */}
          <div className="section">
            <button
              type="button"
              className="favorite-checkbox"
              onClick={() => setFavorite(!favorite)}>
              <span className="checkbox-icon">{favorite ? "☑" : "☐"}</span>
              <div className="favorite-text">
                <strong>Favorite this therapist</strong>
                <p className="favorite-subtitle">
                  We'll prioritize your favorite therapists for scheduled
                  appointments
                </p>
              </div>
              <span className="info-icon">ⓘ</span>
            </button>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="submit-btn"
            disabled={submitting || rating === 0}>
            {submitting ? "Sending..." : "Send"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PostAppointmentFeedback;
